import { useEffect, useState } from "react";
import { TopBar } from "@/components/PageShell";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Calendar, Eye, CheckCircle2, ChevronRight, Clock, FileText, X, Filter, Link2, Link2Off, ShieldAlert, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import PrazosInsights from "@/components/PrazosInsights";

const urgencyStyle = {
  vencido: "border-black bg-black text-white",
  "1h": "border-black bg-black/90 text-white",
  "3h": "border-rose-700 bg-rose-700 text-white",
  "6h": "border-rose-600 bg-rose-600 text-white",
  "24h": "border-rose-500 bg-rose-500 text-white",
  "3d": "border-amber-500 bg-amber-500 text-black",
  "7d": "border-yellow-400 bg-yellow-100 text-yellow-900",
  future: "border-emerald-300 bg-emerald-100 text-emerald-800",
  done: "border-slate-300 bg-slate-100 text-slate-600",
};
const urgencyText = { vencido: "VENCIDO", "1h": "< 1h", "3h": "< 3h", "6h": "< 6h", "24h": "< 24h", "3d": "3 dias", "7d": "semana", future: "futuro", done: "cumprido" };

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit"}) : "—";
const fmtD = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("pt-BR") : "—";

export default function Prazos() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const initialFilter = params.get("filter") || "open";
  const [filter, setFilterState] = useState(initialFilter);
  const [detail, setDetail] = useState(null);
  const [google, setGoogle] = useState(null);
  const highlight = params.get("highlight");

  const setFilter = (f) => {
    setFilterState(f);
    // sincroniza URL para permitir bookmark/deep-link
    const p = new URLSearchParams(params);
    if (f && f !== "open") p.set("filter", f); else p.delete("filter");
    setParams(p, { replace: true });
  };

  // Se URL muda externamente (ex.: vinda do dashboard), sincroniza estado
  useEffect(() => {
    const u = params.get("filter") || "open";
    if (u !== filter) setFilterState(u);
    // eslint-disable-next-line
  }, [params]);

  const load = async () => {
    setLoading(true);
    try {
      const [rs, sm, gs] = await Promise.all([
        api.get("/deadlines", { params: filter === "done" ? { status: "CUMPRIDO" } : {} }),
        api.get("/deadlines/summary"),
        api.get("/integrations/google/status").catch(() => ({ data: null })),
      ]);
      setRows(rs.data.rows || []);
      setSummary(sm.data);
      setGoogle(gs.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const confirmDone = async (id) => {
    try {
      await api.post(`/deadlines/${id}/confirm-completion`);
      toast.success("Prazo cumprido");
      load();
    } catch { toast.error("Falha"); }
  };

  const connectGoogle = async () => {
    try {
      const r = await api.get("/integrations/google/auth-url");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google Calendar indisponível no momento");
    }
  };

  const syncPending = async () => {
    try {
      const r = await api.post("/deadlines/sync-google-pending");
      toast.success(`${r.data.synced} prazos sincronizados${r.data.skipped ? ` · ${r.data.skipped} pulados` : ""}`);
      load();
    } catch { toast.error("Falha ao sincronizar"); }
  };

  const filtered = filter === "done"
    ? rows
    : filter === "open"
    ? rows.filter(r => r.urgency !== "done")
    : rows.filter(r => r.urgency === filter);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="prazos-page">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-rose-600">Radar de Prazos</div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="w-7 h-7 text-rose-600"/> Prazos Processuais
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Motor CPC (dias úteis · art. 219, 220, 224 · feriados nacionais). Prazos identificados
          automaticamente pelo DJEN + IA. Confirme cumprimento para desativar alertas.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="prazos-summary">
          <SummaryCard label="Vencidos" value={summary.buckets.vencido} tone="black" onClick={()=>setFilter("vencido")}/>
          <SummaryCard label="Até 24h" value={summary.buckets["24h"]} tone="rose" onClick={()=>setFilter("24h")}/>
          <SummaryCard label="Próx. 3 dias" value={summary.buckets["3d"]} tone="amber" onClick={()=>setFilter("3d")}/>
          <SummaryCard label="Esta semana" value={summary.buckets["7d"]} tone="yellow" onClick={()=>setFilter("7d")}/>
          <SummaryCard label="Futuros" value={summary.buckets.future} tone="emerald" onClick={()=>setFilter("future")}/>
        </div>
      )}

      {/* Central de Prazos · gráficos operacionais */}
      <PrazosInsights/>

      {google && google.configured && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid="google-cal-status">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
                          ${google.connected ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>
            {google.connected ? <Link2 className="w-4 h-4"/> : <Link2Off className="w-4 h-4"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Google Calendar</div>
            <div className="text-sm text-slate-800">
              {google.connected
                ? <>Conectado como <b>{google.email}</b> — prazos novos criam evento automaticamente com lembrete 3 dias antes.</>
                : "Conecte sua conta para receber os prazos direto na agenda com lembrete de 3 dias."}
            </div>
          </div>
          {google.connected ? (
            <button onClick={syncPending} data-testid="btn-sync-pending"
              className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5"/> Sincronizar pendentes
            </button>
          ) : (
            <button onClick={connectGoogle} data-testid="btn-connect-google"
              className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1">
              <Link2 className="w-3.5 h-3.5"/> Conectar Google Calendar
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {[
          ["open", "Abertos"], ["24h", "< 24h"], ["3d", "3 dias"],
          ["7d", "Semana"], ["future", "Futuros"], ["done", "Cumpridos"], ["vencido", "Vencidos"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={()=>setFilter(k)}
            className={`h-9 px-3 rounded-lg text-xs font-medium border ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`prazos-filter-${k}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando prazos...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="prazos-empty">
            Nenhum prazo neste filtro.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(r => (
              <div key={r.id} className={`px-6 py-4 ${r.id === highlight ? "bg-amber-50" : ""}`} data-testid={`prazo-row-${r.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border-2 ${urgencyStyle[r.urgency] || urgencyStyle.future}`}>
                    {urgencyText[r.urgency] || r.urgency}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-slate-900 truncate flex items-center gap-2">
                      {r.process_number || "—"}
                      {r.confidence && r.confidence !== "alta" && (
                        <span data-testid={`prazo-review-${r.id}`}
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-200"
                              title="Confiança do cálculo abaixo de ALTA — recomenda-se conferência">
                          <ShieldAlert className="w-3 h-3"/> Necessita conferência
                        </span>
                      )}
                      {r.google_event_id ? (
                        <span data-testid={`prazo-gcal-${r.id}`}
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                              title="Sincronizado no Google Calendar">
                          <Calendar className="w-3 h-3"/> Google ✓
                        </span>
                      ) : r.google_sync_status === "SKIPPED" ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200"
                              title={r.google_sync_reason || "Google Calendar não conectado"}>
                          <Calendar className="w-3 h-3"/> pendente
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.court || "—"}{r.court_unit ? " · " + r.court_unit : ""}
                    </div>
                    <div className="text-xs text-slate-700 mt-1 truncate">
                      <strong>{r.expedient_type}</strong>{r.required_action ? " — " + r.required_action : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Vencimento</div>
                    <div className="text-sm font-semibold text-slate-900">{fmtDT(r.deadline_at)}</div>
                  </div>
                  <button onClick={()=>setDetail(r)}
                    className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs inline-flex items-center gap-1"
                    data-testid={`prazo-view-${r.id}`}>
                    <Eye className="w-3.5 h-3.5"/> Ver contagem
                  </button>
                  {r.google_event_url && (
                    <a href={r.google_event_url} target="_blank" rel="noreferrer"
                      className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs inline-flex items-center gap-1"
                      data-testid={`prazo-gcal-open-${r.id}`}>
                      <ExternalLink className="w-3.5 h-3.5"/> Google
                    </a>
                  )}
                  {r.urgency !== "done" && (
                    <button onClick={()=>confirmDone(r.id)}
                      className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold inline-flex items-center gap-1"
                      data-testid={`prazo-done-${r.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5"/> Cumprir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && <TraceModal dl={detail} onClose={()=>setDetail(null)}/>}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone, onClick }) {
  const t = {
    black: "border-black bg-black text-white",
    rose: "border-rose-500 bg-rose-500 text-white",
    amber: "border-amber-400 bg-amber-100 text-amber-900",
    yellow: "border-yellow-300 bg-yellow-50 text-yellow-800",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
  }[tone];
  return (
    <button onClick={onClick} className={`rounded-2xl border-2 p-5 text-left ${t}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-4xl font-black mt-2">{value}</div>
    </button>
  );
}

function TraceModal({ dl, onClose }) {
  const [trace, setTrace] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/deadlines/${dl.id}/trace`).then(r => { if (alive) setTrace(r.data); })
       .catch(() => setTrace({ trace: dl.calc_trace || [], excluded_breakdown: {} }));
    return () => { alive = false; };
  }, [dl.id]);
  const rows = trace?.trace || dl.calc_trace || [];
  const breakdown = trace?.excluded_breakdown || {};
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()} data-testid="prazo-trace-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Como o Hermes calculou este prazo?</div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 font-mono">{dl.process_number}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{dl.court}{dl.court_unit ? " · " + dl.court_unit : ""}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <div><div className="text-xs text-slate-500">Publicação</div><div className="font-medium">{fmtD(dl.publication_date)}</div></div>
            <div><div className="text-xs text-slate-500">Início da contagem</div><div className="font-medium">{fmtD(dl.deadline_start_date)}</div></div>
            <div><div className="text-xs text-slate-500">Prazo</div><div className="font-medium">{dl.deadline_quantity} {dl.deadline_unit}{dl.business_days ? " úteis" : " corridos"}</div></div>
            <div><div className="text-xs text-slate-500">Vencimento</div><div className="font-bold text-rose-700">{fmtDT(dl.deadline_at)}</div></div>
          </div>
          {Object.keys(breakdown).length > 0 && (
            <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Dias não computados</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(breakdown).map(([reason, count]) => (
                  <span key={reason} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                    <b>{count}</b> · {reason}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Dia a dia (art. 219, 220, 224 CPC)</div>
          <div className="rounded-lg border border-slate-200 overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 text-white sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Dia</th>
                  <th className="text-left px-3 py-2">Motivo</th>
                  <th className="text-right px-3 py-2">Contador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className={r.computado ? "" : "bg-slate-50 text-slate-400"}>
                    <td className="px-3 py-1.5 font-mono">{r.data}</td>
                    <td className="px-3 py-1.5 capitalize">{r.dia_semana}</td>
                    <td className="px-3 py-1.5">{r.motivo}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{r.contador || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-[10px] text-slate-500">
            Motor v{dl.calculation_version} · Calendário {dl.calendar_version} · {dl.legal_basis}
            {dl.original_due_date && dl.original_due_date !== dl.deadline_date && (
              <div className="mt-1 text-purple-700 font-semibold">
                Recalculado — data anterior: {fmtD(dl.original_due_date)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
